// src/utils/harToRows.js
//
// WP3 of docs/capture-scoped-ask-spec.md — EXTRACTED (not rewritten) from
// frontend/src/pages/MainApp.js, which used to define `kvToObj`,
// `extractReqBodyFromHar`, `extractRespBodyFromHar`, `harEntryToRow` and
// `harToRows` privately. Moving them here gives the capture-scoped-ask
// server-store path (and anything else that needs "what is a row from a
// HAR") one converter instead of a second parallel answer to that question.
// MainApp.js's `openHarById()` now imports `harToRows` from here.
//
// Bug fixed here — the reason this work package exists: `extractReqBodyFromHar`
// used to hardcode `application/octet-stream` as the `__BINARY__:` marker
// mime whenever a body was base64-encoded, ignoring the HAR's real
// `postData.mimeType` entirely. Third-party HARs (Charles, Proxyman, Chrome
// DevTools) base64-encode ALL bodies, including JSON ones — so a JSON POST
// body arrived wrapped as `__BINARY__:application/octet-stream;base64,...`,
// and `conformance/operators.js`'s `bodyOf()` only parses strings starting
// with `{`, so every body-sourced param (APS `adsdk`, `pj.cstm.cturl`,
// OpenRTB `imp.ext.gpid`) read as ABSENT rather than unknown. `encoding`
// describes how the body was TRANSPORTED in the HAR file; `mimeType` says
// what it IS — getting that backwards is the same class of bug documented in
// the har-ingest-base64 memory. Fix: decode base64 first, then decide using
// the REAL mimeType (never a hardcoded one) plus a JSON-shape sniff (belt
// and braces for a HAR that mislabels mimeType) whether the decoded text can
// be returned directly, falling back to the `__BINARY__:<real-mime>;base64,`
// marker (utils/binaryMarker.js) only for a genuinely binary body.

import { buildBinaryMarker } from './binaryMarker';
import { detectSoundOnAutoplay, CODE as SOUND_ON_CODE } from '../validation/soundOnAutoplay';
import { extractLiftoffEvent } from '../validation/liftoffEvent';
import { stampMappingRecord } from '../validation/sdkBiddingMapping';
import { stampWireFields } from '../validation/metaWire';

// Mirrors decodeBody.js's isTextType convention on the proxy side — the same
// pattern utils/har.js (a different HAR importer, for the HAR-library viewer)
// already established — so a base64-encoded body decodes the same way
// regardless of which import path handles it.
const TEXT_MIME = /(^text\/|json|xml|javascript|x-www-form-urlencoded)/i;

function looksLikeJson(text) {
    if (typeof text !== 'string') return false;
    const s = text.trim();
    return s.startsWith('{') || s.startsWith('[');
}

// Decode a base64 HAR body to UTF-8 text. Callers wrap this in try/catch and
// fall back to the binary marker on failure (malformed base64 in the wild).
function decodeBase64Text(b64) {
    const bin = atob(b64);
    if (typeof TextDecoder !== 'undefined') {
        const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
        return new TextDecoder('utf-8').decode(bytes);
    }
    // TextDecoder isn't available in every runtime that loads this module —
    // notably this project's Jest/jsdom environment, which means the BROWSER
    // takes the branch above and the test suite takes this one. They must
    // therefore agree, so harToRows.test.js pins both by forcing
    // global.TextDecoder on and off; don't drop those two tests.
    // `escape` operates per byte on atob's binary string, so decodeURIComponent
    // reassembles multi-byte UTF-8 correctly — astral-plane code points
    // included (verified: emoji survive both branches identically).
    return decodeURIComponent(escape(bin));
}

function kvToObj(arr) {
    const out = {};
    (arr || []).forEach(h => {
        const k = (h?.name || h?.key || "").toLowerCase();
        if (!k) return;
        out[k] = h?.value ?? h?.val ?? "";
    });
    return out;
}

export function extractReqBodyFromHar(req = {}) {
    const t = req?.postData?.text;
    const enc = req?.postData?.encoding;
    const mime = req?.postData?.mimeType || "";
    if (!t) return "";
    if (enc === "base64") {
        try {
            const decoded = decodeBase64Text(t);
            // encoding said HOW it travelled; mime (+ a JSON-shape sniff as a
            // fallback for mislabeled HARs) says WHAT it is.
            if (TEXT_MIME.test(mime) || looksLikeJson(decoded)) return decoded;
            return buildBinaryMarker(mime || "application/octet-stream", t);
        } catch {
            return buildBinaryMarker(mime || "application/octet-stream", t);
        }
    }
    return t;
}

export function extractRespBodyFromHar(resp = {}) {
    const c = resp?.content || {};
    if (!c.text) return "";
    if (c.encoding === "base64") {
        const mime = c.mimeType || "application/octet-stream";
        try {
            const decoded = decodeBase64Text(c.text);
            if (TEXT_MIME.test(mime) || looksLikeJson(decoded)) return decoded;
            return buildBinaryMarker(mime, c.text);
        } catch {
            return buildBinaryMarker(mime, c.text);
        }
    }
    return c.text;
}

function shortTypeFromUrlOrMime(url = "", mime = "") {
    const u = url.toLowerCase();
    if (u.includes("/gampad/ads") || u.includes("doubleclick.net") || u.includes("securepubads")) return "GAM";
    if (u.includes("/openrtb2/prebid")) return "PREBID";
    if (mime.includes("json")) return "json";
    if (mime.includes("javascript")) return "js";
    return mime || "";
}

// Restore Inspectra metadata from a HAR entry so validations work when a HAR
// is reopened. Throws on a genuinely malformed entry (e.g. null/undefined) —
// harToRows() below counts that as `skipped` instead of aborting the import.
function harEntryToRow(e, idx, harLogInspectra) {
    const startedAt = e.startedDateTime ? Date.parse(e.startedDateTime) : Date.now();
    const method = e.request?.method || "GET";
    const url = e.request?.url || "";
    // USB/logcat rows never had a response — null like the live path (MainApp toNetworkRow)
    const status = e.response?.status || (e._inspectra?.captureSource ? null : 0);
    const durationMs = typeof e.time === "number" ? e.time : undefined;
    const mime = e.response?.content?.mimeType?.toLowerCase() || "";
    const size =
        e.response?.bodySize ??
        e.response?._transferSize ??
        e.response?.content?.size ??
        0;

    const ix = e._inspectra || {};
    const fromLog = (harLogInspectra && harLogInspectra.globalConfig) || {};

    return {
        id: e._id || `${idx}-${startedAt}`,
        method,
        url,
        status,
        type: shortTypeFromUrlOrMime(url, mime),
        startedAt,
        durationMs,
        size,
        requestHeaders: kvToObj(e.request?.headers),
        responseHeaders: kvToObj(e.response?.headers),
        requestBody: extractReqBodyFromHar(e.request),
        responseBody: extractRespBodyFromHar(e.response),

        requestType: ix.requestType || undefined,
        cookies: e.request?.cookies || [],
        contentEncoding: (e.response?.headers || []).find(h => /content-encoding/i.test(h?.name))?.value || undefined,
        responseContentType: mime,
        isBinary: false,

        // bring back computed validation context or log-level fallback
        configAttributes: ix.configAttributes || fromLog || {},
        validData: ix.validData || null,
        contextRef: ix.contextRef || null,
        issues: Array.isArray(ix.issues) ? ix.issues : [],
        winner: ix.winner || null,
        step: ix.step || undefined,   // app-flow step marker (per-tab attribution)
        // USB/logcat provenance (iu-finder-spec Contract E); absent on MITM/old HARs
        captureSource: ix.captureSource || undefined,
        truncated: ix.truncated || undefined,
        // proxy URL rewrite record (buildHar writes it); absent on third-party HARs
        iuRewrite: ix.iuRewrite || undefined,

        // lifecycle / content opportunity signal (restored from _inspectra)
        contentOpportunity: ix.contentOpportunity || null
    };
}

/**
 * harToRows(har) -> { rows, entryCount, skipped, bodiesRetained }
 *
 * Never throws. A malformed HAR (no `log.entries` array) returns
 * `{ rows: [], entryCount: 0, skipped: 0, bodiesRetained: 0 }`. A malformed
 * individual entry increments `skipped` and is dropped rather than aborting
 * the whole import. `bodiesRetained` is the fraction (0..1) of RETURNED rows
 * that actually carry a request body — capture-scoped-ask's absence-gating
 * (WP5) depends on this number being honest.
 */
export function harToRows(har) {
    try {
        const entries = har?.log?.entries;
        if (!Array.isArray(entries)) return { rows: [], entryCount: 0, skipped: 0, bodiesRetained: 0 };

        const logInspectra = har?.log?._inspectra || {};
        const rows = [];
        let skipped = 0;
        entries.forEach((e, i) => {
            try {
                const row = harEntryToRow(e, i, logInspectra);
                // Older HARs predate the server-side sound-on finding — derive it here.
                if (!row.issues.some(x => x && x.code === SOUND_ON_CODE)) {
                    const extra = detectSoundOnAutoplay(row);
                    if (extra.length) row.issues = [...row.issues, ...extra];
                }
                // Same for the compact Liftoff telemetry summary (lifecycle sdkTelemetry tier).
                if (!(row.validData && row.validData.sdkEvent)) {
                    const sdkEvent = extractLiftoffEvent(row);
                    if (sdkEvent) row.validData = { ...(row.validData && typeof row.validData === 'object' ? row.validData : {}), sdkEvent };
                }
                // SDK-bidding sign-off (AC 70): recompute the unit->buyer record from the HAR's
                // own pubsetting body (no-op for any other row, or one with no body).
                stampMappingRecord(row);
                // Meta placement conformance: recompute the wire fields from the bodies
                // (raw HARs have no _inspectra). Twin of qaRunner/collect.js.
                stampWireFields(row);
                rows.push(row);
            } catch {
                skipped += 1;
            }
        });

        const withReqBody = rows.reduce((n, r) => n + (r.requestBody ? 1 : 0), 0);
        const bodiesRetained = rows.length ? withReqBody / rows.length : 0;

        return { rows, entryCount: entries.length, skipped, bodiesRetained };
    } catch {
        return { rows: [], entryCount: 0, skipped: 0, bodiesRetained: 0 };
    }
}
